import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import {
  DayPlanTrackingStatus,
  DayPlanTrackingType,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { FC, useEffect, useRef, useState } from "react";
import { createToast, DropsButton } from "shared/components/Spor";
import { useUserInfo } from "shared/hooks/useUserInfo";

type AcknowledgeManualRequest = {
  username: string;
  employeeId: string;
  date: string;
};

type Props = {
  dayPlanTracking: DayPlanTrackingType;
};

function pathForDayPlanStatus(status: DayPlanTrackingStatus) {
  switch (status) {
    case DayPlanTrackingStatus.MANUAL:
      return "remove-manual-acknowledgement";

    case DayPlanTrackingStatus.SENT:
    case DayPlanTrackingStatus.RECEIVED:
      return "add-manual-acknowledgement";

    case DayPlanTrackingStatus.RECEIVED_BEFORE_START:
    case DayPlanTrackingStatus.READ:
    case DayPlanTrackingStatus.OUTDATED:
      return null;
  }
}

const DayPlanTrackingActionButton: FC<Props> = ({ dayPlanTracking }) => {
  const { status, dayPlanDate, employeeId } = dayPlanTracking;
  const { email } = useUserInfo();

  const [showLoading, setShowLoading] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout>(null);

  const canAcknowledge =
    status === DayPlanTrackingStatus.SENT ||
    status === DayPlanTrackingStatus.RECEIVED;
  const manuallyAcknowledged = status === DayPlanTrackingStatus.MANUAL;

  const { mutate: manualAcknowledgementToggle } = useMutation({
    mutationFn: (body: AcknowledgeManualRequest) =>
      mutationFnPOST<any, AcknowledgeManualRequest>(
        `${getBackendUrl()}/day-plan/trackings/${body.date}/${
          body.employeeId
        }/${pathForDayPlanStatus(dayPlanTracking.status)}?username=${body.username}`,
        body,
      ),
    onMutate: () => {
      setShowLoading(true);
    },
    onSuccess: () => {
      // Show the button as loading until we either get an updated tracking
      // or until the timeout, to prevent a softlock.
      loadingTimeoutRef.current = setTimeout(
        () => setShowLoading(false),
        10_000,
      );
    },
    onError: () => {
      setShowLoading(false);
      createToast({
        variant: "error",
        text: `${canAcknowledge ? "Bekreftelse" : "Gjenåpning"} mislyktes, prøv igjen om litt`,
        closable: true,
      });
    },
  });

  useEffect(() => {
    if (loadingTimeoutRef.current) {
      // Received updated message before timeout; cancel the timeout
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (showLoading) {
      // If an update happens while we're waiting, assume it's the update we were waiting for.
      // The text ternary may seem backwards, but because the dayplantracking has already
      // changed, we have to look at the current value and assume the previous one.
      createToast({
        variant: "success",
        text: manuallyAcknowledged ? "Bekreftelse sendt" : "Gjenåpning sendt",
        duration: 3000,
      });
      setShowLoading(false);
    }
  }, [dayPlanTracking]);

  if (canAcknowledge || manuallyAcknowledged) {
    return (
      <DropsButton
        variant="tertiary"
        size="xs"
        loading={showLoading}
        onClick={(e) => {
          e.stopPropagation();
          manualAcknowledgementToggle({
            username: email,
            date: dayPlanDate,
            employeeId,
          });
        }}
      >
        {canAcknowledge ? "Bekreft" : "Gjenåpne"}
      </DropsButton>
    );
  }

  return null;
};

export default DayPlanTrackingActionButton;
